import type {
    ContentDefinition,
    ImportCachedSymbol,
    ParsedDocument,
    PluginDefinition,
    SerializerTargetLocator,
} from "@magic-md/core";
import { useMemo } from "react";
import { collectParsedDocumentUseBindings } from "../lib/for-scope-units";
import { resolveUseBindingAtLocator } from "../lib/unit-edit-target";
import {
    getMissingRequiredTemplateInputNames,
    resolveUseTemplateSource,
} from "../lib/template-unit-state";
import type { ImportExecutionEntry } from "../store/plugin-execution-store";

export function useDefinitionUseBinding(
    definitionId: string | undefined,
    parsedDocument: ParsedDocument | undefined,
    definitions: Map<string, PluginDefinition>,
    contentDefinitions: Map<string, ContentDefinition>,
    importExecutions?: Map<string, ImportExecutionEntry>,
    target?: {
        content?: string;
        locator?: SerializerTargetLocator;
    },
) {
    const allUses = useMemo(
        () => collectParsedDocumentUseBindings(parsedDocument),
        [parsedDocument],
    );

    const currentUseBinding = useMemo(
        () => {
            const located = definitionId && target?.content && target.locator
                ? resolveUseBindingAtLocator({
                    content: target.content,
                    useId: definitionId,
                    locator: target.locator,
                })
                : undefined;
            return located ?? allUses.find(
                (useBinding) => useBinding.id === definitionId,
            );
        },
        [definitionId, allUses, target?.content, target?.locator],
    );

    const useSourceConfig = useMemo(() => {
        if (!currentUseBinding) return undefined;
        const sourceDefinition = definitions.get(currentUseBinding.source);
        return sourceDefinition?.config as Record<string, unknown> | undefined;
    }, [currentUseBinding, definitions]);

    const importSymbols = useMemo<ImportCachedSymbol[] | undefined>(() => {
        const symbols: ImportCachedSymbol[] = [];
        for (const importBinding of parsedDocument?.imports ?? []) {
            if (importBinding.output?.symbols) symbols.push(...importBinding.output.symbols);
        }
        for (const importExecution of importExecutions?.values() ?? []) {
            if (importExecution.symbols) {
                symbols.push(...(importExecution.symbols as ImportCachedSymbol[]));
            }
        }
        return symbols.length > 0 ? symbols : undefined;
    }, [parsedDocument?.imports, importExecutions]);

    const templateSource = useMemo(() => {
        if (!currentUseBinding) return undefined;

        return resolveUseTemplateSource(
            currentUseBinding.source,
            definitions,
            contentDefinitions,
            parsedDocument?.forDefinitions,
            allUses,
            importSymbols,
        );
    }, [currentUseBinding, definitions, contentDefinitions, parsedDocument?.forDefinitions, allUses, importSymbols]);

    const templateParams = templateSource?.params;
    const templateInputSchema = templateSource?.inputSchema;
    const missingRequiredTemplateInputs = useMemo(
        () =>
            currentUseBinding
                ? getMissingRequiredTemplateInputNames(
                    currentUseBinding.with,
                    templateInputSchema,
                    templateParams,
                )
                : [],
        [currentUseBinding, templateInputSchema, templateParams],
    );

    const useSourceContent = useMemo(() => {
        if (!currentUseBinding) return undefined;

        const localContent = contentDefinitions.get(currentUseBinding.source);
        if (localContent) {
            return {
                id: currentUseBinding.source,
                template: !!localContent.template,
                imported: false,
            };
        }

        if (!importSymbols) return undefined;

        const matchedSymbol = importSymbols.find(
            (symbol) =>
                symbol.kind === "content"
                && (symbol.qualifiedId === currentUseBinding.source
                    || symbol.publicName === currentUseBinding.source),
        );
        if (!matchedSymbol) return undefined;

        return {
            id: currentUseBinding.source,
            template: !!matchedSymbol.template,
            imported: true,
        };
    }, [currentUseBinding, contentDefinitions, importSymbols]);

    return {
        currentUseBinding,
        useSourceConfig,
        importSymbols,
        templateParams,
        templateInputSchema,
        missingRequiredTemplateInputs,
        useSourceContent,
    };
}
